"use client";

import { useEffect, useMemo, useState } from "react";
import "./VendorPickerModal.css";

/* -------------------------------------------------------------------------- */
/* Types                                                                        */
/* -------------------------------------------------------------------------- */

/** An entry in the vendor library passed to the picker. */
export type VendorRecord = {
  id: string;
  name: string;
  category: string;
  description: string;
  tags: string[];
};

/** A selected vendor reference (subset of VendorRecord, may be custom). */
export type Vendor = {
  id: string;
  name: string;
  category: string;
  /** True when the entry was added by the user outside the vetted library. */
  custom?: boolean;
};

export interface VendorPickerModalProps {
  /** Catalogue of vendors available for browsing. */
  vendors: VendorRecord[];
  /** Currently-selected vendors (controlled). */
  selected: Vendor[];
  /** Close the modal without side-effects. */
  onClose: () => void;
  /** Add a vendor to the selection. */
  onAdd: (v: Vendor) => void;
  /** Remove a vendor from the selection by id. */
  onRemove: (id: string) => void;
}

/* -------------------------------------------------------------------------- */
/* Pure helpers                                                                 */
/* -------------------------------------------------------------------------- */

/** Derive 1–2 uppercase initials from a vendor name. */
function vendorInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2)
    return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

/** Deterministic pastel background + dark text for a vendor icon. */
function vendorIconStyle(id: string): React.CSSProperties {
  const hues = [220, 260, 150, 200, 30, 300, 340, 180];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  const hue = hues[h % hues.length];
  return {
    background: `hsl(${hue} 60% 88%)`,
    color: `hsl(${hue} 50% 30%)`,
  };
}

/* -------------------------------------------------------------------------- */
/* Component                                                                    */
/* -------------------------------------------------------------------------- */

type Tab = "browse" | "custom";

/**
 * Full-screen modal for picking vendors from a library or adding custom entries.
 * All data and state callbacks are supplied via props — no internal databases.
 */
export function VendorPickerModal({
  vendors,
  selected,
  onClose,
  onAdd,
  onRemove,
}: VendorPickerModalProps) {
  const [tab, setTab] = useState<Tab>("browse");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string>("All");
  const [customName, setCustomName] = useState("");
  const [customCategory, setCustomCategory] = useState("");

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const categories = useMemo(
    () => Array.from(new Set(vendors.map((v) => v.category))).sort(),
    [vendors],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return vendors.filter((v) => {
      if (category !== "All" && v.category !== category) return false;
      if (!q) return true;
      const hay =
        `${v.name} ${v.category} ${v.description} ${v.tags.join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [vendors, query, category]);

  const selectedIds = new Set(selected.map((s) => s.id));

  function toggle(record: VendorRecord) {
    if (selectedIds.has(record.id)) {
      onRemove(record.id);
    } else {
      onAdd({ id: record.id, name: record.name, category: record.category });
    }
  }

  function submitCustom() {
    const name = customName.trim();
    if (!name) return;
    const cat = customCategory.trim() || "Custom";
    const id = `vnd-custom-${Date.now()}`;
    onAdd({ id, name, category: cat, custom: true });
    setCustomName("");
    setCustomCategory("");
    setTab("browse");
  }

  return (
    <div
      className="vpm-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Add vendor"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        // Handle keyboard-equivalent of backdrop click (Enter/Space).
        // Escape is intentionally omitted here — it is handled exclusively by
        // the window keydown listener in the useEffect above to prevent
        // onClose from being called twice on a single keypress.
        if (
          (e.key === "Enter" || e.key === " ") &&
          e.target === e.currentTarget
        )
          onClose();
      }}
    >
      <div className="vpm-modal">
        <header className="vpm-head">
          <div>
            <h2>Add specific vendors</h2>
            <p className="vpm-sub">Add vendors for consideration.</p>
          </div>
          <button
            type="button"
            className="vpm-close"
            onClick={onClose}
            aria-label="Close"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6l-12 12" />
            </svg>
          </button>
        </header>

        <div className="vpm-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "browse"}
            className={`vpm-tab${tab === "browse" ? " active" : ""}`}
            onClick={() => setTab("browse")}
          >
            Browse library
            <span className="vpm-tab-count">{vendors.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "custom"}
            className={`vpm-tab${tab === "custom" ? " active" : ""}`}
            onClick={() => setTab("custom")}
          >
            Add your own
          </button>
        </div>

        {tab === "browse" ? (
          <>
            <div className="vpm-toolbar">
              <div className="vpm-search">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  placeholder="Search vendors, categories, or tags"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  // biome-ignore lint/a11y/noAutofocus: picker modal — expected
                  autoFocus
                />
              </div>
              <select
                className="vpm-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="All">All categories ({vendors.length})</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c} ({vendors.filter((v) => v.category === c).length})
                  </option>
                ))}
              </select>
            </div>

            <ul className="vpm-list">
              {filtered.length === 0 ? (
                <li>
                  <div className="vpm-empty">
                    No vendors match. Try a different search, or{" "}
                    <button
                      type="button"
                      className="vpm-link"
                      onClick={() => setTab("custom")}
                    >
                      add your own
                    </button>
                    .
                  </div>
                </li>
              ) : (
                filtered.map((v) => {
                  const isSelected = selectedIds.has(v.id);
                  return (
                    <li key={v.id}>
                      <button
                        type="button"
                        className={`vpm-row${isSelected ? " selected" : ""}`}
                        onClick={() => toggle(v)}
                        aria-pressed={isSelected}
                      >
                        <span className="vpm-check" aria-hidden="true">
                          {isSelected && (
                            <svg
                              width="11"
                              height="11"
                              viewBox="0 0 12 12"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M2.5 6.5l2.5 2.5 4.5-5.5" />
                            </svg>
                          )}
                        </span>
                        <span
                          className="vpm-row-icon"
                          style={vendorIconStyle(v.id)}
                          aria-hidden="true"
                        >
                          {vendorInitials(v.name)}
                        </span>
                        <div className="vpm-row-main">
                          <div className="vpm-row-name">{v.name}</div>
                          <div className="vpm-row-desc">{v.description}</div>
                        </div>
                        <div className="vpm-row-meta">
                          <span className="vpm-row-cat">{v.category}</span>
                          <div className="vpm-row-tags">
                            {v.tags.map((t) => (
                              <span key={t} className="vpm-row-tag">
                                {t}
                              </span>
                            ))}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          </>
        ) : (
          <div className="vpm-custom">
            <div className="vpm-field">
              <label htmlFor="vpm-custom-name">Vendor name</label>
              <input
                id="vpm-custom-name"
                type="text"
                placeholder="e.g. Acme Health AI"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                // biome-ignore lint/a11y/noAutofocus: custom-tab — expected
                autoFocus
              />
            </div>
            <div className="vpm-field">
              <label htmlFor="vpm-custom-cat">Category (optional)</label>
              <input
                id="vpm-custom-cat"
                type="text"
                placeholder="e.g. Patient access, CDI, Imaging AI"
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    submitCustom();
                  }
                }}
              />
            </div>
            <p className="vpm-custom-hint">
              Custom vendors get flagged in the brief so reviewers know the
              entry came from outside the vetted library.
            </p>
            <div className="vpm-custom-actions">
              <button
                type="button"
                className="vpm-secondary"
                onClick={() => setTab("browse")}
              >
                Back to library
              </button>
              <button
                type="button"
                className="vpm-primary"
                onClick={submitCustom}
                disabled={!customName.trim()}
              >
                Add vendor
              </button>
            </div>
          </div>
        )}

        <footer className="vpm-foot">
          <div className="vpm-foot-count">
            {selected.length === 0
              ? "No vendors selected yet"
              : selected.length === 1
                ? "1 vendor selected"
                : `${selected.length} vendors selected`}
          </div>
          <button type="button" className="vpm-primary" onClick={onClose}>
            Done
          </button>
        </footer>
      </div>
    </div>
  );
}
